import React from 'react';
import * as yup from 'yup';
import Checkbox from '../../../Components/Base/Checkbox/Checkbox';
import DiagnosisSelectField from '../../../Components/Base/Diagnosis/DiagnosisSelectField';
import Select from '../../../Components/Base/Select/Select';
import TextArea from '../../../Components/Base/TextArea/TextArea';
import TextField from '../../../Components/Base/TextField/TextField';
import SubmitButton from '../../../Components/Button/SubmitButton';
import NumberField from '../../../Components/Form/components/NumberField';
import FormControl from '../../../Components/Form/FormControl';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import useTranslation from '../../../Hooks/useTranslation';
import TermItemSelectField from '../../Term/Components/TermItemSelectField';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWrapper from '../../../Components/Form/FormWrapper';
import useNotification from '../../../Hooks/useNotification.hook';
import { getSafeValue } from '../../../Utils/form.util';
import InputField from '../../../Components/Form/components/InputField';
import SuklRouteOfAdministrationFiled from './Fields/SuklRouteOfAdministration/SuklRouteOfAdministrationField';
import InputWrapper from '../../../Components/Form/components/InputWrapper/InputWrapper';
import SuklFormField from './Fields/SuklForm/SuklFormField';
import { getPaymentOptions } from '../Services/eRecept.service';

export interface EPrescriptionIPLPMedicament {
	id: string;
	formType: PrescriptionType;
	name: string;
	routeOfAdministration?: {
		label: string;
		value: string;
	};
	form?: {
		label: string;
		value: string;
	};
	quantity: number;
	payment?: {
		label: string;
		value: string;
	};
	manual: string;
	requestNumber?: number;
	doNotConfuse?: boolean;
	overrun?: boolean;
	instruction: string;
	diagnosis?: {
		label: string;
		value: string;
	};
	associatedDiagnosis: {
		label: string;
		value: string;
	};
}

interface EPrescriptionMedicamentsIPLPFormProps {
	onSubmit?: (values: EPrescriptionIPLPMedicament) => void;
	defaultValues?: Record<string, any>;
	patientId?: string;
	isSelfPayer: boolean;
	submitButtonText?: string;
}

const EPrescriptionMedicamentsIPLPForm: React.FC<
	EPrescriptionMedicamentsIPLPFormProps
> = ({ onSubmit, defaultValues, patientId, isSelfPayer, submitButtonText }) => {
	const { t } = useTranslation();
	const { showNotificationAlert } = useNotification();

	const handleSave = (values: any) => {
		const payment = values.payment ?? null;
		const data: EPrescriptionIPLPMedicament = {
			id: defaultValues?.id ?? uuidv4(),
			formType: PrescriptionType.IPLP,
			name: values.name,
			routeOfAdministration: values.routeOfAdministration ?? null,
			form: values.form ?? null,
			quantity: values.quantity,
			payment,
			manual: values.manual,
			requestNumber: values.requestNumber ?? null,
			doNotConfuse: values.doNotConfuse,
			overrun: values.overrun,
			instruction: values.instruction,
			diagnosis: values.diagnosis ?? null,
			associatedDiagnosis: values.associatedDiagnosis,
		};
		onSubmit?.(data);
	};

	const validationSchema = yup.object({
		name: yup
			.string()
			.required(t('validation.ePrescriptionMedicamentsIPLPForm.name')),
		routeOfAdministration: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		form: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		quantity: yup
			.number()
			.typeError(
				t('validation.ePrescriptionMedicamentsIPLPForm.quantity') ?? '',
			)
			.required(t('validation.ePrescriptionMedicamentsIPLPForm.quantity')),
		payment: yup
			.object({
				label: yup.string(),
				value: yup.string().required('Úhrada je povinná'),
			})
			.required(''),
		manual: yup
			.string()
			.required(t('validation.ePrescriptionMedicamentsIPLPForm.dosage')),
		requestNumber: yup.mixed().notRequired(),
		doNotConfuse: yup.boolean().notRequired(),
		overrun: yup.boolean().notRequired(),
		instruction: yup
			.string()
			.required(t('validation.ePrescriptionMedicamentsIPLPForm.instruction')),
		diagnosis: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		associatedDiagnosis: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
	});

	const units = [
		{ label: t('terms.mj.g') ?? '', value: 'g' },
		{ label: t('terms.mj.ks') ?? '', value: 'ks' },
	];

	const paymentOptions = getPaymentOptions(isSelfPayer);

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	return (
		<FormWrapper formMethods={formMethods}>
			<FormControl
				label='component.ePrescription.nameOfMeds'
				name='name'
				isRequired>
				<TextField />
			</FormControl>
			<InputWrapper>
				<SuklRouteOfAdministrationFiled
					label='component.ePrescription.howToAdministrate'
					name='routeOfAdministration'
				/>
			</InputWrapper>
			<InputWrapper>
				<SuklFormField label='component.ePrescription.form' name='form' />
			</InputWrapper>
			<NumberField
				label='component.ePrescription.amount'
				name='quantity'
				isRequired
			/>
			<FormControl
				label='component.ePrescription.payment'
				name='payment'
				isRequired>
				<Select options={paymentOptions} />
			</FormControl>
			<FormControl
				label='component.ePrescription.dosage'
				name='manual'
				isRequired>
				<TextField />
			</FormControl>
			<NumberField
				label='component.ePrescription.applicationNumber'
				name='requestNumber'
			/>
			<div className='row'>
				<div className='col-md-6'>
					<FormControl
						label='component.ePrescription.notToBeMistaken'
						name='doNotConfuse'>
						<Checkbox />
					</FormControl>
				</div>
				<div className='col-md-6'>
					<FormControl label='component.ePrescription.overStep' name='overrun'>
						<Checkbox />
					</FormControl>
				</div>
			</div>
			<FormControl
				label='component.ePrescription.preparationProcedure'
				name='instruction'
				isRequired>
				<TextArea rows={6} />
			</FormControl>
			<FormControl label='base.shared.diagnosis' name='diagnosis'>
				<DiagnosisSelectField name='diagnosis' portal={false} />
			</FormControl>
			<FormControl
				label='component.ePrescription.additionalDiagnosis'
				name='associatedDiagnosis'>
				<DiagnosisSelectField name='associatedDiagnosis' portal={false} />
			</FormControl>
			<div className='d-flex mt-4 justify-content-between'>
				<SubmitButton
					title={submitButtonText ?? 'base.shared.continue'}
					onSubmit={handleSave}
				/>
			</div>
		</FormWrapper>
	);
};

export default EPrescriptionMedicamentsIPLPForm;
